import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { 
  MessageCircle, X, Send, Loader2, Bot, User, 
  Sparkles, Minimize2, Maximize2, ThumbsUp, ThumbsDown
} from 'lucide-react';
import { toast } from 'sonner';

export const AIAssistant = () => {
  const { api, user, isAuthenticated, loading: authLoading } = useAuth();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  // Posição arrastável persistida em localStorage.
  // Default: bottom-right (24px de margem).
  const [pos, setPos] = useState(() => {
    try {
      const raw = localStorage.getItem('bluegov_assistant_pos');
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return { right: 24, bottom: 96 };  // px do canto inferior-direito
  });
  const dragRef = useRef({ active: false, startX: 0, startY: 0, startRight: 0, startBottom: 0, moved: false });
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `Olá${user?.name ? `, ${user.name.split(' ')[0]}` : ''}! 👋\n\nSou o Assistente BlueGov, estou aqui para ajudar você a:\n\n• Entender atos de controle\n• Navegar pelo sistema\n• Analisar prazos e riscos\n• Sugerir ações\n\nComo posso ajudar?`,
      chat_id: null
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // ============== Drag-and-drop do botão flutuante ==============
  const onDragStart = (e) => {
    const ev = e.touches ? e.touches[0] : e;
    dragRef.current = {
      active: true,
      startX: ev.clientX,
      startY: ev.clientY,
      startRight: pos.right,
      startBottom: pos.bottom,
      moved: false,
    };
  };
  const onDragMove = (e) => {
    if (!dragRef.current.active) return;
    const ev = e.touches ? e.touches[0] : e;
    const dx = ev.clientX - dragRef.current.startX;
    const dy = ev.clientY - dragRef.current.startY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragRef.current.moved = true;
    // bottom-right base: subtrair dx do right, dy do bottom
    const newRight = Math.max(0, Math.min(window.innerWidth - 64, dragRef.current.startRight - dx));
    const newBottom = Math.max(0, Math.min(window.innerHeight - 64, dragRef.current.startBottom - dy));
    // Se a janela do chat estiver aberta, clamp considerando a altura/largura do painel
    // para impedir que ele saia da viewport durante o arrasto.
    if (isOpen && !isMinimized && window.innerWidth >= 768) {
      const maxBottomChat = Math.max(0, window.innerHeight - 512 - 16);
      const maxRightChat = Math.max(0, window.innerWidth - 384 - 16);
      setPos({
        right: Math.min(maxRightChat, Math.max(16, newRight)),
        bottom: Math.min(maxBottomChat, Math.max(16, newBottom)),
      });
      return;
    }
    setPos({ right: newRight, bottom: newBottom });
  };
  const onDragEnd = () => {
    if (!dragRef.current.active) return;
    dragRef.current.active = false;
    try { localStorage.setItem('bluegov_assistant_pos', JSON.stringify(pos)); } catch { /* ignore */ }
  };
  // O clique abre o chat APENAS se não houve arrasto.
  const onButtonClick = () => {
    if (dragRef.current.moved) {
      dragRef.current.moved = false;
      return;
    }
    setIsOpen(true);
  };
  // ============================================================

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isMinimized]);

  // ----- Clamp da posição: garante que a janela inteira caiba na viewport.
  // Altura desejada da janela (mantém em sync com a classe Tailwind 32rem = 512px).
  const CHAT_HEIGHT_PX = 512;
  const CHAT_WIDTH_PX = 384; // md:w-96
  const SAFE_MARGIN = 16;    // px de respiro do topo/laterais

  const clampPosition = (p) => {
    if (typeof window === 'undefined') return p;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Bottom: garante que o topo da janela (bottom + altura) fique dentro da viewport.
    const maxBottom = Math.max(0, vh - CHAT_HEIGHT_PX - SAFE_MARGIN);
    const minBottom = SAFE_MARGIN;
    // Right: garante que a janela inteira caiba na largura.
    const maxRight = Math.max(0, vw - CHAT_WIDTH_PX - SAFE_MARGIN);
    const minRight = SAFE_MARGIN;
    return {
      right: Math.min(maxRight, Math.max(minRight, p.right)),
      bottom: Math.min(maxBottom, Math.max(minBottom, p.bottom)),
    };
  };

  // Reposiciona ao abrir o chat (persiste apenas quando muda).
  // Calcula a posição efetiva (clamped) no render para evitar setState em effect.
  const effectivePos = (isOpen && !isMinimized) ? clampPosition(pos) : pos;

  useEffect(() => {
    // Persiste o clamp em localStorage se a posição foi ajustada.
    if (!isOpen || isMinimized) return;
    if (effectivePos.right === pos.right && effectivePos.bottom === pos.bottom) return;
    try { localStorage.setItem('bluegov_assistant_pos', JSON.stringify(effectivePos)); } catch { /* ignore */ }
  }, [isOpen, isMinimized, pos, effectivePos]);

  // Reposiciona quando a viewport mudar de tamanho (resize/rotate).
  useEffect(() => {
    const onResize = () => {
      setPos((prev) => clampPosition(prev));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const params = new URLSearchParams({ message: userMessage });
      if (sessionId) params.append('session_id', sessionId);

      const response = await api.post(`/assistant/chat?${params.toString()}`);
      
      if (response.data.session_id) {
        setSessionId(response.data.session_id);
      }

      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response,
        error: response.data.error,
        chat_id: response.data.chat_id || `chat_${Date.now()}`
      }]);

    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Desculpe, ocorreu um erro. Tente novamente.',
        error: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleFeedback = async (msgIndex, feedbackType) => {
    const msg = messages[msgIndex];
    if (!msg.chat_id || msg.feedback) return;

    try {
      await api.post('/assistant/feedback', {
        chat_id: msg.chat_id,
        feedback: feedbackType
      });

      setMessages(prev => prev.map((m, idx) => 
        idx === msgIndex ? { ...m, feedback: feedbackType } : m
      ));

      toast.success(feedbackType === 'positive' 
        ? 'Obrigado pelo feedback positivo!' 
        : 'Obrigado! Vamos melhorar.'
      );
    } catch (error) {
      console.error('Feedback error:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleQuickAction = (text) => {
    setInput(text);
    setTimeout(() => sendMessage(), 100);
  };

  const quickActions = [
    { text: 'O que são atos de controle?', icon: '📋' },
    { text: 'Como usar a IA de classificação?', icon: '🤖' },
    { text: 'Tenho atos vencidos?', icon: '⏰' },
  ];

  // Hide assistant on landing page, login page, bluegov admin pages, and contract page
  const publicPaths = ['/', '/login', '/contrato', '/bluegov'];
  const isPublicPage = publicPaths.some(path => 
    location.pathname === path || location.pathname.startsWith('/bluegov')
  );

  if (authLoading || !isAuthenticated || isPublicPage) return null;

  return (
    <>
      {/* Floating Button (arrastável) */}
      {!isOpen && (
        <button
          onClick={onButtonClick}
          onMouseDown={onDragStart}
          onMouseMove={onDragMove}
          onMouseUp={onDragEnd}
          onMouseLeave={onDragEnd}
          onTouchStart={onDragStart}
          onTouchMove={onDragMove}
          onTouchEnd={onDragEnd}
          style={{ right: `${pos.right}px`, bottom: `${pos.bottom}px`, touchAction: 'none', userSelect: 'none', cursor: 'grab' }}
          className="fixed w-14 h-14 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full shadow-lg hover:shadow-xl transition-shadow duration-300 flex items-center justify-center z-[9999] group"
          data-testid="ai-assistant-btn"
          aria-label="Assistente BlueGov (arrastável)"
        >
          <MessageCircle className="w-6 h-6 group-hover:scale-110 transition-transform pointer-events-none" />
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse pointer-events-none" />
        </button>
      )}

      {/* Chat Window (posicionado conforme botão; arrasta junto via mesma posição) */}
      {isOpen && (
        <Card
          className={`fixed z-[9999] shadow-2xl border-0 transition-shadow duration-300 flex flex-col ${
            isMinimized
              ? 'w-72 h-14'
              : 'w-full md:w-96 md:rounded-lg rounded-t-2xl'
          }`}
          style={{
            right: window.innerWidth >= 768 ? `${effectivePos.right}px` : 0,
            bottom: window.innerWidth >= 768 ? `${effectivePos.bottom}px` : 0,
            left: window.innerWidth >= 768 ? 'auto' : 0,
            // Altura: até 32rem em desktop, mas nunca maior do que o espaço disponível
            // acima do bottom; mobile usa 85vh ancorado na base.
            height: isMinimized
              ? undefined
              : (window.innerWidth >= 768
                  ? `min(32rem, calc(100vh - ${effectivePos.bottom}px - 16px))`
                  : '85vh'),
            overflow: 'hidden',
          }}
          data-testid="ai-assistant-window"
        >
          {/* Header — também serve como handle de arrasto (desktop) */}
          <div
            className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 flex items-center justify-between flex-shrink-0 cursor-grab active:cursor-grabbing select-none"
            onMouseDown={onDragStart}
            onMouseMove={onDragMove}
            onMouseUp={onDragEnd}
            onMouseLeave={onDragEnd}
            onTouchStart={onDragStart}
            onTouchMove={onDragMove}
            onTouchEnd={onDragEnd}
            style={{ touchAction: 'none' }}
            data-testid="ai-assistant-drag-handle"
            title="Arraste para mover (desktop)"
          >
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white font-medium text-sm">Assistente BlueGov</p>
                {!isMinimized && (
                  <p className="text-white/70 text-xs">Online</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/20"
                onClick={() => setIsMinimized(!isMinimized)}
              >
                {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/20"
                onClick={() => setIsOpen(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
                {messages.map((msg, idx) => (
                  <div key={idx}>
                    <div className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                          msg.role === 'user'
                            ? 'bg-blue-600 text-white rounded-br-md'
                            : msg.error 
                              ? 'bg-red-50 text-red-700 border border-red-200 rounded-bl-md'
                              : 'bg-white text-slate-700 shadow-sm border border-slate-100 rounded-bl-md'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      </div>
                      {msg.role === 'user' && (
                        <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-slate-600" />
                        </div>
                      )}
                    </div>
                    
                    {/* Feedback buttons for assistant messages */}
                    {msg.role === 'assistant' && msg.chat_id && idx > 0 && (
                      <div className="flex items-center gap-1 mt-1 ml-1">
                        {msg.feedback ? (
                          <span className="text-xs text-slate-400">
                            {msg.feedback === 'positive' ? 'Obrigado!' : 'Vamos melhorar'}
                          </span>
                        ) : (
                          <>
                            <button
                              onClick={() => handleFeedback(idx, 'positive')}
                              className="p-1 rounded hover:bg-green-50 text-slate-400 hover:text-green-600 transition-colors"
                              title="Resposta útil"
                            >
                              <ThumbsUp className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleFeedback(idx, 'negative')}
                              className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors"
                              title="Resposta não útil"
                            >
                              <ThumbsDown className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                
                {loading && (
                  <div className="flex gap-2 justify-start">
                    <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 shadow-sm border border-slate-100">
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>

              {/* Quick Actions */}
              {messages.length === 1 && !loading && (
                <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 flex-shrink-0">
                  <p className="text-xs text-slate-500 mb-2">Sugestões rápidas:</p>
                  <div className="flex flex-wrap gap-2">
                    {quickActions.map((action, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleQuickAction(action.text)}
                        className="text-xs bg-white border border-slate-200 rounded-full px-3 py-1.5 hover:bg-slate-50 hover:border-blue-300 transition-colors active:scale-95"
                      >
                        {action.text}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Input */}
              <div className="p-3 pb-4 bg-white border-t border-slate-200 flex-shrink-0 mb-10 md:mb-0">
                <div className="flex gap-2 items-center">
                  <Input
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Digite sua pergunta..."
                    className="flex-1 border-slate-200 focus:border-blue-500 focus:ring-blue-500/20 h-11 text-base"
                    disabled={loading}
                    data-testid="ai-assistant-input"
                    autoComplete="off"
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={!input.trim() || loading}
                    className="bg-blue-600 hover:bg-blue-700 h-11 w-11 p-0 flex-shrink-0"
                    data-testid="ai-assistant-send"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card>
      )}
    </>
  );
};

export default AIAssistant;
